import { useNavigate } from "@tanstack/react-router";
import { Fragment, useRef } from "react";

import { Table, TableBody } from "@/components/ui/table";
import { cn } from "@/lib/cn";

import { getDetailUrlWithParams } from "./constants";
import { DataRow } from "./DataRow";
import { DateGroupHeaderCell } from "./DateGroupHeaderCell";
import { EmptyDataRow } from "./EmptyDataRow";
import { Footer } from "./Footer";
import {
  useCategories,
  useColumnIndexer,
  useDataGrouping,
  useHoverState,
} from "./hooks";
import { TableHeaderComponent } from "./TableHeader";
import type { AggregateTableProps } from "./types";

export const AggregateTable = ({
  coreType,
  className,
  data,
  operationPackageId,
  patternId,
  emptyDataMessage = "No data",
}: AggregateTableProps) => {
  const navigate = useNavigate();
  const tableRef = useRef<HTMLTableElement>(null);

  // データとカテゴリの処理
  const { groupedData, expandedGroups, toggleGroupExpansion } =
    useDataGrouping(data);
  const categories = useCategories(data);

  // カラムインデックスの管理
  const columnIndexer = useColumnIndexer(categories);

  // ホバー状態の管理
  const {
    hoveredRowIndex,
    hoveredColIndex,
    setHoveredColIndex,
    handleCellMouseEnter,
    handleCellMouseLeave,
  } = useHoverState();

  // 行クリック時の詳細ページへの遷移
  const handleRowClick = (id: string) => {
    const detailUrl = getDetailUrlWithParams(
      coreType,
      operationPackageId,
      patternId,
      id,
    );
    navigate({ to: detailUrl });
  };

  return (
    <div className={cn("h-full flex flex-col", className)}>
      <div className="flex-1 overflow-auto">
        <Table ref={tableRef} className="w-full table-auto">
          <TableHeaderComponent
            categories={categories}
            hoveredColIndex={hoveredColIndex}
            setHoveredColIndex={setHoveredColIndex}
            columnIndexer={columnIndexer}
          />
          <TableBody className="relative">
            {Object.entries(groupedData).map(([date, rows], groupIndex) => (
              <Fragment key={date}>
                <DateGroupHeaderCell
                  date={date}
                  categories={categories}
                  expandedGroups={expandedGroups}
                  toggleGroupExpansion={toggleGroupExpansion}
                  hoveredColIndex={hoveredColIndex}
                  handleCellMouseEnter={handleCellMouseEnter}
                  handleCellMouseLeave={handleCellMouseLeave}
                  columnIndexer={columnIndexer}
                />
                {expandedGroups[date] &&
                  rows.map((row, rowIndex) => {
                    const globalRowIndex = groupIndex * 100 + rowIndex; // 一意の行インデックスを生成(hover effectのため)
                    return (
                      <DataRow
                        key={row.id}
                        row={row}
                        rowIndex={globalRowIndex}
                        categories={categories}
                        hoveredRowIndex={hoveredRowIndex}
                        hoveredColIndex={hoveredColIndex}
                        handleRowClick={handleRowClick}
                        handleCellMouseEnter={handleCellMouseEnter}
                        handleCellMouseLeave={handleCellMouseLeave}
                        columnIndexer={columnIndexer}
                      />
                    );
                  })}
              </Fragment>
            ))}
            {data.length === 0 && (
              <EmptyDataRow
                emptyDataMessage={emptyDataMessage}
                columnCount={columnIndexer.getTotalColumnCount()}
              />
            )}
          </TableBody>
        </Table>
      </div>
      <Footer />
    </div>
  );
};
